import { useState, useRef, useEffect, useCallback } from 'react';
import { useVerificationStore } from '@stores/verificationStore';
import { useSessionStore } from '@stores/sessionStore';
import { requestOtp, verifyOtp, getPhoneHint, requestEmailOtp, verifyEmailOtp, getEmailHint, requestDeviceOtp, verifyDeviceOtp } from '@services/authService';
import './PhoneVerificationModal.scss';

type Step = 'method' | 'phone' | 'email' | 'code';
type VerifyMethod = 'phone' | 'email';

export default function PhoneVerificationModal() {
  const { showModal, closeModal, completeVerification, pending, mode } = useVerificationStore();
  const { updateTrustLevel, refreshSession } = useSessionStore();

  const [step, setStep] = useState<Step>('method');
  const [verifyMethod, setVerifyMethod] = useState<VerifyMethod>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+972');
  const [challengeId, setChallengeId] = useState('');
  const [maskedDestination, setMaskedDestination] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Phone hint state — when hotel has a phone on file for the guest
  const [phoneOnFile, setPhoneOnFile] = useState(false);
  const [phoneHintMask, setPhoneHintMask] = useState('');
  // Email hint state — when hotel has an email on file for the guest
  const [emailOnFile, setEmailOnFile] = useState(false);
  const [emailHintMask, setEmailHintMask] = useState('');
  const [hintLoading, setHintLoading] = useState(false);
  const [deliveryChannel, setDeliveryChannel] = useState<'WhatsApp' | 'Sms' | 'Email' | 'Dev' | null>(null);

  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // Reset state and fetch phone/email hints when modal opens
  useEffect(() => {
    if (showModal) {
      setVerifyMethod('phone');
      setPhone('');
      setEmail('');
      setCode(['', '', '', '', '', '']);
      setError('');
      setCooldown(0);
      setChallengeId('');
      setPhoneOnFile(false);
      setPhoneHintMask('');
      setEmailOnFile(false);
      setEmailHintMask('');

      if (pending) {
        // A code was already sent (e.g. pushed by the front desk after check-in).
        // Jump straight to code entry — verification happens against the latest
        // pending challenge for this session (no challengeId needed).
        setStep('code');
        setDeliveryChannel(pending.channel === 'WhatsApp' ? 'WhatsApp' : 'Sms');
        setMaskedDestination(pending.maskedPhone || '');
        setResendCooldown(60);
        setTimeout(() => codeRefs.current[0]?.focus(), 300);
      } else if (mode === 'device') {
        // "Verify this device to order": the room phone is already on file (session is verified) and
        // the OTP must go to it — skip method/email selection and go straight to the phone step.
        setVerifyMethod('phone');
        setStep('phone');
        setMaskedDestination('');
        setResendCooldown(0);
        setDeliveryChannel(null);
      } else {
        setStep('method');
        setMaskedDestination('');
        setResendCooldown(0);
        setDeliveryChannel(null);
      }

      // Fetch phone and email hints from API (used on the phone/email steps and resend)
      setHintLoading(true);
      Promise.all([getPhoneHint(), getEmailHint()]).then(([phoneHint, emailHint]) => {
        if (phoneHint.hasPhoneOnFile && phoneHint.maskedPhone) {
          setPhoneOnFile(true);
          setPhoneHintMask(phoneHint.maskedPhone);
        }
        if (emailHint.hasEmailOnFile && emailHint.maskedEmail) {
          setEmailOnFile(true);
          setEmailHintMask(emailHint.maskedEmail);
        }
        setHintLoading(false);
      });
    }
  }, [showModal, pending]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSendCode = useCallback(async () => {
    // When phone is on file, guest enters the full E.164 number (e.g. +972528347049)
    // When no phone on file, construct from country code + local number
    const fullPhone = phoneOnFile ? phone.trim() : `${countryCode}${phone.replace(/^0+/, '')}`;

    if (phoneOnFile) {
      // Must be a valid E.164 number starting with +
      if (!phone.trim().match(/^\+[1-9]\d{6,14}$/)) {
        setError('Please enter the full phone number including country code (e.g. +972528347049).');
        return;
      }
    } else {
      if (phone.length < 7) {
        setError('Please enter a valid phone number.');
        return;
      }
    }

    setIsLoading(true);
    setError('');

    // Device mode = "verify this device to order": OTP to the ROOM phone, sets THIS device's TrustedAt.
    const result = mode === 'device' ? await requestDeviceOtp(fullPhone) : await requestOtp(fullPhone);

    if (result.success && result.challengeId) {
      setChallengeId(result.challengeId);
      setDeliveryChannel(result.deliveryChannel || 'Sms');
      setCooldown(0);
      setResendCooldown(60);
      setMaskedDestination(result.maskedPhone || fullPhone.replace(/(\+\d{3})\d+(\d{3})/, '$1****$2'));
      setStep('code');
      setCode(['', '', '', '', '', '']);
      setTimeout(() => codeRefs.current[0]?.focus(), 300);
    } else {
      setError(result.error || 'Failed to send code.');
      // Check if there's a cooldown in the error response
      if (result.error?.includes('wait')) {
        setCooldown(60);
      }
    }
    setIsLoading(false);
  }, [phone, countryCode, phoneOnFile, mode]);

  const handleSendEmailCode = useCallback(async () => {
    const emailValue = email.trim();
    
    if (!emailValue.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    setError('');

    const result = await requestEmailOtp(emailValue);

    if (result.success && result.challengeId) {
      setChallengeId(result.challengeId);
      setMaskedDestination(result.maskedPhone || emailValue.replace(/^(.{2}).*(@.*)$/, '$1***$2'));
      setDeliveryChannel('Email');
      setCooldown(0);
      setResendCooldown(60);
      setStep('code');
      setCode(['', '', '', '', '', '']);
      setTimeout(() => codeRefs.current[0]?.focus(), 300);
    } else {
      setError(result.error || 'Failed to send code.');
      if (result.error?.includes('wait')) {
        setCooldown(60);
      }
    }
    setIsLoading(false);
  }, [email]);

  const handleCodeChange = useCallback((index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    
    // Handle paste of full code
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      digits.forEach((d, i) => { newCode[i] = d; });
      setCode(newCode);
      const focusIdx = Math.min(digits.length, 5);
      codeRefs.current[focusIdx]?.focus();
      // Auto-submit if all 6 digits pasted
      if (digits.length === 6) {
        handleVerify(newCode);
      }
      return;
    }

    newCode[index] = value;
    setCode(newCode);

    // Auto-advance to next input
    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }

    // Auto-submit when last digit entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(newCode);
      }
    }
  }, [code]);

  const handleCodeKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  }, [code]);

  const handleVerify = useCallback(async (codeArr?: string[]) => {
    const fullCode = (codeArr || code).join('');
    if (fullCode.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }

    setIsLoading(true);
    setError('');

    // Use the correct verify function: device mode sets THIS device's trust; session/email upgrade session trust.
    const result = mode === 'device'
      ? await verifyDeviceOtp(challengeId, fullCode)
      : verifyMethod === 'email'
        ? await verifyEmailOtp(challengeId, fullCode)
        : await verifyOtp(challengeId, fullCode);

    if (result.success && result.verified) {
      // Device OTP does NOT change session trust — only this device's TrustedAt. Session/email OTP do.
      if (mode !== 'device') {
        updateTrustLevel(result.trustLevel || 2);
      }
      // Refresh session from server so deviceTrusted (and trustLevel) sync and gates unlock without reload.
      refreshSession();
      completeVerification();
    } else {
      setError(result.error || 'Invalid code. Please try again.');
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
    }
    setIsLoading(false);
  }, [code, challengeId, verifyMethod, mode, updateTrustLevel, refreshSession, completeVerification]);

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0) return;

    // Pending (staff-pushed) code: the app doesn't hold the full phone number, so send the
    // guest to the phone step to confirm their number and request a fresh code.
    if (pending && !challengeId) {
      setStep('phone');
      setError('');
      setCode(['', '', '', '', '', '']);
      return;
    }

    setIsLoading(true);
    setError('');

    let result;
    if (verifyMethod === 'email') {
      result = await requestEmailOtp(email.trim());
    } else {
      const fullPhone = phoneOnFile ? phone.trim() : `${countryCode}${phone.replace(/^0+/, '')}`;
      result = mode === 'device' ? await requestDeviceOtp(fullPhone) : await requestOtp(fullPhone);
    }

    if (result.success && result.challengeId) {
      setChallengeId(result.challengeId);
      setDeliveryChannel(verifyMethod === 'email' ? 'Email' : (result.deliveryChannel || 'Sms'));
      setResendCooldown(60);
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
    } else {
      setError(result.error || 'Failed to resend code.');
    }
    setIsLoading(false);
  }, [verifyMethod, email, phone, countryCode, phoneOnFile, resendCooldown, pending, challengeId, mode]);

  if (!showModal) return null;

  return (
    <div className="verification-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }} role="dialog" aria-modal="true" aria-label="Verify your identity">
      <div className="verification-modal">
        {/* Close button */}
        <button className="verification-close" onClick={closeModal} aria-label="Close">
          &times;
        </button>

        {/* Step: Method Selection */}
        {step === 'method' && (
          <>
            <div className="verification-icon">🔐</div>
            <h3 className="verification-title">Verify Your Identity</h3>
            {hintLoading ? (
              <p className="verification-subtitle">Loading...</p>
            ) : (
              <>
                <p className="verification-subtitle">
                  Choose how you'd like to receive your verification code:
                </p>
                <div className="method-buttons">
                  <button
                    className="method-btn"
                    onClick={() => { setVerifyMethod('phone'); setStep('phone'); setTimeout(() => phoneRef.current?.focus(), 300); }}
                  >
                    <span className="method-icon">📱</span>
                    <span className="method-label">Phone (WhatsApp/SMS)</span>
                    {phoneOnFile && <span className="method-hint">{phoneHintMask}</span>}
                  </button>
                  {/* Only offer email when there's one on file to verify against — otherwise there's
                      nothing to match the entered address to (verification is reservation-bound). */}
                  {emailOnFile && (
                    <button
                      className="method-btn"
                      onClick={() => { setVerifyMethod('email'); setStep('email'); setTimeout(() => emailRef.current?.focus(), 300); }}
                    >
                      <span className="method-icon">✉️</span>
                      <span className="method-label">Email</span>
                      <span className="method-hint">{emailHintMask}</span>
                    </button>
                  )}
                </div>
              </>
            )}
            <button className="verification-btn link" onClick={closeModal}>
              Skip for now
            </button>
          </>
        )}

        {/* Step: Phone Input */}
        {step === 'phone' && (
          <>
            <div className="verification-icon">📱</div>
            <h3 className="verification-title">{mode === 'device' ? 'Verify to charge to your room' : 'Verify Your Phone'}</h3>
            {mode === 'device' && (
              <p className="verification-subtitle">
                Enter the room phone number to receive a code and unlock room charges &amp; folio on this device.
              </p>
            )}

            {hintLoading ? (
              <p className="verification-subtitle">Loading...</p>
            ) : phoneOnFile ? (
              <>
                <p className="verification-subtitle">
                  Please enter the full phone number for your reservation:
                </p>
                <div className="phone-hint-badge">
                  {phoneHintMask}
                </div>
                <div className="phone-input-group">
                  <input
                    ref={phoneRef}
                    type="tel"
                    className="phone-input"
                    style={{ width: '100%' }}
                    placeholder="+972528347049"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value.replace(/[^\d+]/g, ''));
                      setError('');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                    maxLength={16}
                  />
                </div>
              </>
            ) : (
              <>
                <p className="verification-subtitle">
                  We'll send a one-time code via WhatsApp (or SMS if unavailable) to verify your room access.
                </p>
                <div className="phone-input-group">
                  <select
                    className="country-select"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                  >
                    <option value="+972">🇮🇱 +972</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+7">🇷🇺 +7</option>
                    <option value="+90">🇹🇷 +90</option>
                    <option value="+971">🇦🇪 +971</option>
                    <option value="+966">🇸🇦 +966</option>
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+86">🇨🇳 +86</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+82">🇰🇷 +82</option>
                    <option value="+55">🇧🇷 +55</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+31">🇳🇱 +31</option>
                    <option value="+30">🇬🇷 +30</option>
                    <option value="+48">🇵🇱 +48</option>
                  </select>
                  <input
                    ref={phoneRef}
                    type="tel"
                    className="phone-input"
                    placeholder="Phone number"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value.replace(/[^\d]/g, ''));
                      setError('');
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                    maxLength={15}
                  />
                </div>
              </>
            )}

            {error && <div className="verification-error">{error}</div>}

            <button
              className="verification-btn primary"
              onClick={handleSendCode}
              disabled={isLoading || hintLoading || (phoneOnFile ? !phone.trim().match(/^\+[1-9]\d{6,14}$/) : phone.length < 7) || cooldown > 0}
            >
              {isLoading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : cooldown > 0 ? (
                `Wait ${cooldown}s`
              ) : (
                'Send Code'
              )}
            </button>

            <button className="verification-btn link" onClick={closeModal}>
              Skip for now
            </button>
            <button className="verification-btn link" onClick={() => setStep('method')}>
              ← Choose another method
            </button>
          </>
        )}

        {/* Step: Email Input */}
        {step === 'email' && (
          <>
            <div className="verification-icon">✉️</div>
            <h3 className="verification-title">Verify Your Email</h3>

            {emailOnFile ? (
              <>
                <p className="verification-subtitle">
                  Please enter the email address for your reservation:
                </p>
                <div className="phone-hint-badge">
                  {emailHintMask}
                </div>
              </>
            ) : (
              <p className="verification-subtitle">
                We'll send a one-time code to your email to verify access.
              </p>
            )}
            
            <div className="phone-input-group">
              <input
                ref={emailRef}
                type="email"
                className="phone-input"
                style={{ width: '100%' }}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSendEmailCode()}
                maxLength={256}
              />
            </div>

            {error && <div className="verification-error">{error}</div>}

            <button
              className="verification-btn primary"
              onClick={handleSendEmailCode}
              disabled={isLoading || !email.trim().match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) || cooldown > 0}
            >
              {isLoading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : cooldown > 0 ? (
                `Wait ${cooldown}s`
              ) : (
                'Send Code'
              )}
            </button>

            <button className="verification-btn link" onClick={closeModal}>
              Skip for now
            </button>
            <button className="verification-btn link" onClick={() => setStep('method')}>
              ← Choose another method
            </button>
          </>
        )}

        {/* Step: Code Entry */}
        {step === 'code' && (
          <>
            <div className="verification-icon">🔐</div>
            <h3 className="verification-title">Enter Code</h3>
            <p className="verification-subtitle">
              Sent via {deliveryChannel === 'WhatsApp' ? '📱 WhatsApp' : deliveryChannel === 'Email' ? '✉️ Email' : '📨 SMS'} to <strong>{maskedDestination}</strong>
            </p>

            <div className="code-input-group">
              {code.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { codeRefs.current[i] = el; }}
                  type="tel"
                  inputMode="numeric"
                  className="code-digit"
                  value={digit}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    if (pasted) handleCodeChange(0, pasted);
                  }}
                  maxLength={1}
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            {error && <div className="verification-error">{error}</div>}

            <button
              className="verification-btn primary"
              onClick={() => handleVerify()}
              disabled={isLoading || code.join('').length !== 6}
            >
              {isLoading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                'Verify'
              )}
            </button>

            <div className="verification-actions">
              <button
                className="verification-btn link"
                onClick={handleResend}
                disabled={resendCooldown > 0 || isLoading}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Didn't receive it? Resend"}
              </button>
              <button
                className="verification-btn link"
                onClick={() => { setStep(verifyMethod); setError(''); setCode(['', '', '', '', '', '']); }}
              >
                ← {verifyMethod === 'email' ? 'Change email' : 'Change number'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
